import * as React from "react";

import Divider from "@mui/material/Divider";
import { styled } from "@mui/material/styles";

import { MyOrgContext } from "context/myorgcontext";
import { UserContext } from "context/usercontext";
import Loader from "src/common/components/Loader";
import useBridgeSWR from "src/common/hooks/BridgeSWRClient";
import useForm, { FormValidationError } from "src/common/hooks/UseForm";
import { useUpdateWorkflow, UpdateWorkflowDTO } from "src/domain/core/workflow/api/updateWorkflow";
import {
  WorkflowConfigFormInput,
  WorkflowConfigFormError,
} from "src/domain/core/workflow/components/update/forms/ConfigForm";
import { UpdateWorkflowForm } from "src/domain/core/workflow/components/update/forms/UpdateForm";
import { endpoint } from "src/lib/bridgeClient";
import * as Tags from "src/lib/tags";

const Title = styled("div")(({ theme }) => ({
  ...theme.typography.h6,
  marginBottom: theme.spacing(1),
  color: theme.palette.text.primary,
}));

const Note = styled("div")(({ theme }) => ({
  ...theme.typography.body2,
  marginBottom: theme.spacing(6),
  color: theme.palette.text.secondary,
}));

export type UpdateWorkflowData = {
  id: string;
  name: string;
  head_node_instance_type: MachineInstanceTypeDesc;
  compute_instance_type: MachineInstanceTypeDesc;
  max_queue_size: number;
  initial_queue_size: number;
  maintain_initial_size: boolean;
  access_from?: string;
  scheduler?: string;
  tags?: Record<string, string>;
};

interface APIData {
  max_queue_size: number;
  use_public_ips: boolean;
}

interface UpdateWorkflowProps {
  currentData: UpdateWorkflowData;
  computeNodes: MachineInstanceTypeDesc[];
  headNodes: MachineInstanceTypeDesc[];
  stackID: number;
  submitCb: () => void;
  cancelCb: () => void;
}

const LoadingContainer = styled("div")(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",

  // This height allows the "loading" modal to approximate the height of the full form,
  // which makes the transition from loading-to-form smoother.
  height: "85vh",
}));

const loadingState = (message: string) => {
  return (
    <LoadingContainer>
      <div style={{}} key="loader">
        <Loader />
      </div>
      <span key="loader-message">{message}</span>
    </LoadingContainer>
  );
};

const UpdateWorkflow: React.FC<UpdateWorkflowProps> = ({
  currentData,
  computeNodes,
  headNodes,
  stackID,
  submitCb,
  cancelCb,
}) => {
  const myUser = React.useContext(UserContext);
  const myOrg = React.useContext(MyOrgContext);

  const updateWorkflow = useUpdateWorkflow();

  const { data: blueprint, error: swrError } = useBridgeSWR<WorkflowBlueprint>(
    stackID ? endpoint.stackByID(stackID) : null
  );

  const { data: blueprintActions, error: actionsError } = useBridgeSWR<BlueprintActions>(
    stackID ? endpoint.stackActionsByID(stackID) + `?scheduler=${currentData?.scheduler}` : null
  );

  const apiData: APIData = {
    use_public_ips: myOrg?.data.use_public_ips,
    max_queue_size: myOrg?.data.max_cluster_size || blueprint?.max_queue_size,
  };

  const [values, setValues] = React.useState<WorkflowConfigFormInput>({
    head_node_instance_type: currentData.head_node_instance_type,
    compute_instance_type: currentData.compute_instance_type,
    max_queue_size: currentData.max_queue_size,
    initial_queue_size: currentData.initial_queue_size,
    maintain_initial_size: currentData.maintain_initial_size,
    access_from: currentData.access_from,
  });

  const [error, setError] = React.useState<WorkflowConfigFormError>({
    initial_queue_size: { has: false },
    max_queue_size: { has: false },
    access_from: { has: false },
  });

  const schema = validationSchema(values, apiData, myUser?.is_power_user);
  const { validate } = useForm<WorkflowConfigFormInput>({
    validations: schema,
    data: values,
    setError: setError,
  });

  if (swrError || actionsError) {
    return <div>Unable to load data about stack</div>;
  }

  if (!blueprint || !blueprintActions) {
    return loadingState("");
  }

  const workflowDisplayName = currentData.tags?.[Tags.workflowDisplayName] || currentData.name;

  const submit = async (values: WorkflowConfigFormInput) => {
    let workflow: UpdateWorkflowDTO = {
      head_node_instance_type: values.head_node_instance_type?.instance_type,
      compute_instance_type: values.compute_instance_type?.instance_type,
      access_from: values.access_from,
      max_queue_size: undefined,
      initial_queue_size: undefined,
      maintain_initial_size: undefined,
    };

    if (myUser?.is_power_user) {
      workflow = {
        ...workflow,
        initial_queue_size: values.initial_queue_size,
        maintain_initial_size: values.maintain_initial_size,
        max_queue_size: values.max_queue_size,
      };
    }

    const resp = await updateWorkflow(currentData.id, workflowDisplayName, workflow);
    if (resp && resp.ok) {
      submitCb();
    }

    return resp;
  };

  const getNote = () => {
    if (blueprintActions?.head_node_update_enabled) {
      return (
        <Note>
          Updating Head Node will cause a brief outage while the instance is moved to new hardware. Running processes
          will be terminated.
        </Note>
      );
    }

    return <Note>Cluster version for your workflow does not support updating head node.</Note>;
  };

  return (
    <>
      <Title>{workflowDisplayName}</Title>
      <Divider />
      {getNote()}
      <UpdateWorkflowForm
        apiData={{
          ...apiData,
          scheduler: currentData.scheduler,
          head_node_instance_types: headNodes,
          compute_instance_types: computeNodes,
          maintain_initial_size: currentData.maintain_initial_size,
          actions: {
            head_node_update_enabled: blueprintActions?.head_node_update_enabled,
            maintain_initial_size_enabled: blueprintActions?.maintain_initial_size_enabled,
          },
        }}
        values={values}
        setValues={setValues}
        error={error}
        setError={setError}
        validate={validate}
        onSubmit={submit}
        cb={cancelCb}
      />
    </>
  );
};

export default UpdateWorkflow;

const validationSchema = (values: WorkflowConfigFormInput, apiData: APIData, isPowerUser: boolean) => {
  const validation: { [k: string]: unknown } = {
    access_from: {
      pattern:
        "^(([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5]).){3}([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])(/(3[0-2]|[1-2][0-9]|[0-9]))$",
      custom: () => {
        if (!values.access_from) {
          return new FormValidationError("IP address is required (Format: x.x.x.x/x)");
        }

        return;
      },
    },
  };

  if (isPowerUser) {
    validation.max_queue_size = {
      required: true,
      custom: () => {
        if (values.max_queue_size) {
          // Not valid if maximum size provided is higher than allowed for that organization
          if (values.max_queue_size > apiData?.max_queue_size) {
            return new FormValidationError(`Size exceeds max allowed (max: ${apiData.max_queue_size})`);
          }

          if (values.max_queue_size < 1) {
            // Not valid if maximum size provided is less than 1
            return new FormValidationError("Size must be at least 1");
          }

          return;
        }
      },
    };

    validation.initial_queue_size = {
      custom: () => {
        // Not valid if maximum size provided is higher than allowed for that organization
        if (values.initial_queue_size) {
          if (values.initial_queue_size > apiData?.max_queue_size) {
            return new FormValidationError(`Size exceeds max allowed (max: ${apiData.max_queue_size})`);
          }

          if (values.initial_queue_size < 0) {
            return new FormValidationError("Cannot be a negative number");
          }

          if (values.initial_queue_size > values.max_queue_size) {
            return new FormValidationError("Initial queue size must be less than or equal to max queue size");
          }
        }

        return;
      },
    };
  }

  return validation;
};
